import { useQuery } from "@tanstack/react-query"
import { Link, useParams } from "react-router-dom"

import { Button } from "@/components/ui/button"
import { api } from "@/lib/client"
import { formatAddress, formatDateLabel, formatModifiers, windowLabel } from "@/lib/format"
import { queryKeys } from "@/lib/queryKeys"

export function WorkOrderPrintPage() {
  const { date = "", orderId = "" } = useParams()
  const query = useQuery({
    queryFn: () => api.workOrder(orderId, date),
    queryKey: queryKeys.workOrder(orderId, date),
  })
  const doc = query.data?.workOrder

  if (query.isPending) return <p className="text-sm text-muted-foreground">Loading…</p>
  if (!doc) return <p className="text-sm text-destructive">Work order not found.</p>

  return (
    <div className="mx-auto max-w-2xl space-y-6 p-6 print-root">
      <div className="no-print flex items-center justify-between">
        <Button asChild size="sm" variant="ghost">
          <Link to={`/orders/${orderId}`}>Back to order</Link>
        </Button>
        <Button onClick={() => window.print()} size="sm" type="button">
          Print
        </Button>
      </div>
      <header className="border-b border-border pb-4">
        <p className="text-xs tracking-[0.2em] text-muted-foreground uppercase">Haute Organic · Kitchen</p>
        <h1 className="mt-2 text-2xl font-semibold">Work order {doc.orderName}</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {formatDateLabel(doc.date)} · {windowLabel(doc.window)}
        </p>
      </header>
      <section className="text-sm">
        <p className="font-medium">{doc.customer.name}</p>
        <p className="text-muted-foreground">{formatAddress(doc.address)}</p>
        {doc.allergens.length > 0 ? (
          <p className="mt-2 font-medium text-amber-300">Allergens: {doc.allergens.join(", ")}</p>
        ) : null}
      </section>
      {doc.meals.map((meal) => (
        <section key={meal.mealTime}>
          <h2 className="mb-2 text-sm font-semibold tracking-wider uppercase">{meal.mealTime}</h2>
          <ul className="space-y-1 text-sm">
            {meal.lines.map((line) => (
              <li key={line.id}>
                {line.quantity}× {line.name}
                {formatModifiers(line.modifiers) ? ` · ${formatModifiers(line.modifiers)}` : ""}
              </li>
            ))}
          </ul>
        </section>
      ))}
      {doc.components.length > 0 ? (
        <section>
          <h2 className="mb-2 text-sm font-semibold tracking-wider uppercase">Pull list</h2>
          <ul className="space-y-1 text-sm">
            {doc.components.map((c) => (
              <li key={c.productId}>
                {c.quantity} {c.unit} {c.name}
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </div>
  )
}
